import { AlertBox } from '@/components/alerts/AlertBox';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

type AlertLinkProps = {
  to: string;
  children: ReactNode;
};

const AlertLink = ({ to, children }: AlertLinkProps) => {
  return (
    <Link className="text-sm text-link" to={to}>
      {' '}
      {children}
    </Link>
  );
};

export const AccountVerifiedAlert = () => {
  return (
    <AlertBox type="success" title="Account Verified">
      Your account has been successfully activated. You can now log in.
    </AlertBox>
  );
};

export const ActivationRequiredAlert = () => {
  return (
    <AlertBox type="error" title="Activation required">
      Your account needs to be activated so an activation email has been resent. Please follow the
      link in the email to activate your account.
    </AlertBox>
  );
};

export const EmailNotFoundAlert = () => {
  return (
    <AlertBox type="error" title="Email address not found">
      <AlertLink to="/register">Click here</AlertLink> to create an account.
    </AlertBox>
  );
};

export const ForgotPasswordSuccessAlert = () => {
  return (
    <AlertBox type="success" title="Submitted successfully">
      Check your email for a link to reset your password.
    </AlertBox>
  );
};

export const IncorrectEmailPasswordAlert = () => {
  return (
    <AlertBox type="error" title="Incorrect email or password">
      Please try again. You can also
      <AlertLink to="/forgot-password">reset your password</AlertLink>
    </AlertBox>
  );
};

export const InvalidTokenAlert = () => {
  return (
    <AlertBox type="error" title="Invalid activation link">
      Invalid activation link. Please request a new activation email.
    </AlertBox>
  );
};

export const PasswordExpiredAlert = () => {
  return (
    <AlertBox type="error" title="Password reset required">
      Your password has expired, please check your email to set a new password.
    </AlertBox>
  );
};

export const LinkExpiredAlert = () => {
  return (
    <AlertBox type="error" title="Link Expired">
      This link has expired. Please request a new activation link by clicking on
      <AlertLink to="/forgot-password">forgot password</AlertLink>.
    </AlertBox>
  );
};

export const PasswordResetSuccessfulAlert = () => {
  return (
    <AlertBox type="success" title="Password Reset">
      Please log in with your new password.
    </AlertBox>
  );
};

export const RegistrationSuccessAlert = () => {
  return (
    <AlertBox type="success" title="Account Created">
      Check your email for a verification link. You must click the link in your email to activate
      your account.
    </AlertBox>
  );
};

export const TokenExpiredAlert = () => {
  return (
    <AlertBox type="error" title="Link Expired">
      This link has expired. Please request a new activation link by clicking on
      <AlertLink to="/forgot-password">forgot password</AlertLink>.
    </AlertBox>
  );
};

export const UnexpectedErrorAlert = () => {
  return (
    <AlertBox type="error" title="Unexpected Error">
      An unexpected error occurred. Please try again later.
    </AlertBox>
  );
};

export const UserAlreadyExistsAlert = () => {
  return (
    <AlertBox type="error" title="Account Already Exists">
      An account with this email already exists.
      <AlertLink to="/login">Click here</AlertLink> to log in.
    </AlertBox>
  );
};

export const UserNotFoundAlert = () => {
  return (
    <AlertBox type="error" title="User account not found">
      User account not found. Please contact support.
    </AlertBox>
  );
};

type EmailChangeSuccessAlertProps = {
  email?: string;
};

export const EmailChangeSuccessAlert = ({ email }: EmailChangeSuccessAlertProps) => {
  return (
    <AlertBox type="success" title="Confirm Your New Email">
      Please check your new email {email ? <strong>{email}</strong> : 'your new email address'} for
      a confirmation link.
    </AlertBox>
  );
};

export const EmailChangeEmailExistsAlert = () => {
  return (
    <AlertBox type="error" title="Email Already Exists">
      This email address is already registered to another account.
    </AlertBox>
  );
};

export const EmailChangeSecurityNoteAlert = () => {
  return (
    <AlertBox type="warning">
      For your security, please enter your password to authorize this change. A verification link
      will be sent to your new address to confirm this email change.
    </AlertBox>
  );
};

export const ChangeEmailConfirmLinkExpiredAlert = () => {
  return (
    <AlertBox type="error" title="Link Expired">
      This link has expired. Please request a new link by clicking on
      <AlertLink to="/change-email">change email</AlertLink>.
    </AlertBox>
  );
};

type SecondaryEmailExistsAlertProps = {
  email?: string;
};

export const SecondaryEmailExistsAlert = ({ email }: SecondaryEmailExistsAlertProps) => {
  return (
    <AlertBox type="error" title="Email Already Exists">
      You already have an account{email ? `: ${email}` : ''}. Please log in with{' '}
      {email ? 'this email address' : 'your primary email address'}.
    </AlertBox>
  );
};
